/**
 * scenarios.ts — Income and expense projections per scenario and year.
 *
 * All values are sourced from the user's SimulationParams.profile at runtime.
 * No personal constants are hardcoded here — HC-DEFAULTS-IMMUTABLE applies.
 */

import type { SimulationParams } from './types'
import type { YearEventModifiers } from './events'
import { DEFAULT_INCOME_GROWTH_RATE } from './defaults'

export interface IncomeProfile {
  w2Wages: number          // W-2 taxable wages (salary + bonus + equity comp)
  businessIncome: number   // self-employment income
  grossIncome: number      // w2Wages + businessIncome
}

export interface ExpenseProfile {
  housing: number          // housing (mortgage PITI or rent)
  other: number            // all other spending
  total: number
}

/**
 * Returns the income profile for a given simulation year offset (0-indexed).
 * Year 0 = simulation start. Grows at DEFAULT_INCOME_GROWTH_RATE per year.
 * If an incomeEvent is enabled and has taken effect, uses those values instead.
 */
export function incomeForYear(params: SimulationParams, yearOffset: number, evMod?: YearEventModifiers): IncomeProfile {
  const { profile, incomeEvent } = params
  const growthFactor = Math.pow(1 + DEFAULT_INCOME_GROWTH_RATE, yearOffset)

  let w2: number
  let equity: number

  if (incomeEvent.enabled && yearOffset >= incomeEvent.year) {
    const eventGrowth = Math.pow(1 + DEFAULT_INCOME_GROWTH_RATE, yearOffset - incomeEvent.year)
    w2 = (incomeEvent.newAnnualIncome > 0 ? incomeEvent.newAnnualIncome : profile.annualW2Income) * eventGrowth
    const eventEquityActive = incomeEvent.newEquityComp > 0
      && (profile.equityCompYears === 0 || yearOffset < profile.equityCompYears)
    equity = eventEquityActive
      ? (incomeEvent.newEquityComp > 0 ? incomeEvent.newEquityComp : profile.annualEquityComp) * eventGrowth
      : 0
  } else {
    w2 = profile.annualW2Income * growthFactor
    const equityActive = profile.equityCompYears === 0 || yearOffset < profile.equityCompYears
    equity = equityActive ? profile.annualEquityComp * growthFactor : 0
  }

  // Apply life event overrides / multipliers
  if (evMod) {
    if (evMod.incomeOverride !== null) w2 = evMod.incomeOverride
    else w2 *= evMod.incomeMultiplier
    if (evMod.equityOverride !== null) equity = evMod.equityOverride
    else if (evMod.incomeMultiplier < 1) equity *= evMod.incomeMultiplier
  }

  const w2Wages = w2 + equity
  const businessIncome = profile.annualSelfEmploymentIncome

  return { w2Wages, businessIncome, grossIncome: w2Wages + businessIncome }
}

/**
 * Returns the base expense profile for a given simulation year offset.
 * Inflation adjustment is applied by the caller in montecarlo.ts.
 */
export function expensesForYear(params: SimulationParams, _yearOffset: number): ExpenseProfile {
  const { housing: h, other: o } = {
    housing: params.profile.annualHousingExpense,
    other: params.profile.annualOtherExpenses,
  }
  return { housing: h, other: o, total: h + o }
}

